// Rules of the Poker stall: five-card draw, original implementation.

export const SUITS = ['S', 'H', 'D', 'C'];
export const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' };
export const RANK_LABEL = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
export const rankLabel = (rank) => RANK_LABEL[rank] || String(rank);

export const HAND_NAMES = [
  'High Card',
  'One Pair',
  'Two Pair',
  'Three of a Kind',
  'Straight',
  'Flush',
  'Full House',
  'Four of a Kind',
  'Straight Flush',
];

export function newDeck() {
  const deck = [];
  for (const suit of SUITS) for (let rank = 2; rank <= 14; rank++) deck.push({ rank, suit });
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

/** Counts of each rank, as [rank, count] sorted by count then rank (high first). */
function groups(hand) {
  const counts = {};
  hand.forEach((c) => {
    counts[c.rank] = (counts[c.rank] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([rank, count]) => [Number(rank), count])
    .sort((a, b) => b[1] - a[1] || b[0] - a[0]);
}

/** Highest card of a straight, or 0. The ace may also play low (A-2-3-4-5). */
function straightHigh(ranks) {
  const unique = [...new Set(ranks)].sort((a, b) => b - a);
  if (unique.length !== 5) return 0;
  if (unique[0] - unique[4] === 4) return unique[0];
  if (unique.join() === '14,5,4,3,2') return 5;
  return 0;
}

/** Scores a five-card hand: { category (0-8), tiebreak [..], name }. */
export function evaluate(hand) {
  const g = groups(hand);
  const ranks = hand.map((c) => c.rank);
  const flush = hand.every((c) => c.suit === hand[0].suit);
  const straight = straightHigh(ranks);
  const shape = g.map(([, n]) => n).join('');
  const byGroup = g.map(([r]) => r);

  let category;
  let tiebreak = byGroup;
  if (straight && flush) {
    category = 8;
    tiebreak = [straight];
  } else if (shape === '41') category = 7;
  else if (shape === '32') category = 6;
  else if (flush) category = 5;
  else if (straight) {
    category = 4;
    tiebreak = [straight];
  } else if (shape === '311') category = 3;
  else if (shape === '221') category = 2;
  else if (shape === '2111') category = 1;
  else category = 0;
  return { category, tiebreak, name: HAND_NAMES[category] };
}

/** > 0 when a beats b. */
export function compareHands(a, b) {
  if (a.category !== b.category) return a.category - b.category;
  for (let i = 0; i < Math.max(a.tiebreak.length, b.tiebreak.length); i++) {
    const d = (a.tiebreak[i] || 0) - (b.tiebreak[i] || 0);
    if (d) return d;
  }
  return 0;
}

/** Indexes a computer player throws away (at most 3). */
export function botDiscards(hand) {
  const score = evaluate(hand);
  if (score.category >= 4) return []; // straight, flush or better: keep everything
  const g = groups(hand);
  if (g[0][1] >= 2) {
    // keep every card that is part of a pair / three / four
    const keep = new Set(g.filter(([, n]) => n >= 2).map(([r]) => r));
    return hand.map((c, i) => (keep.has(c.rank) ? -1 : i)).filter((i) => i >= 0).slice(0, 3);
  }
  // nothing: keep the two highest cards
  return hand
    .map((c, i) => [c.rank, i])
    .sort((a, b) => a[0] - b[0])
    .slice(0, 3)
    .map(([, i]) => i);
}

/**
 * A computer player's betting choice: 'fold' | 'call' | 'raise'.
 * `bold` is the player's personality (0-1).
 */
export function botBet(hand, { toCall, canRaise, bold }) {
  const { category } = evaluate(hand);
  const roll = Math.random();
  if (toCall === 0) {
    if (canRaise && (category >= 2 || (category === 1 && roll < 0.25 + bold * 0.3) || roll < bold * 0.08)) return 'raise';
    return 'call';
  }
  if (category === 0 && roll < 0.55 - bold * 0.25) return 'fold';
  if (canRaise && category >= 3 && roll < 0.35 + bold * 0.4) return 'raise';
  return 'call';
}
